import "../assets/scss/components/_delivery-form-new.scss";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faMap } from "@fortawesome/pro-regular-svg-icons/faMap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useRef } from "react";

import { AddressApi } from "../api/AddressApi";
import { useProductStore } from "../stores/ProductStore";
import type { DeliveryFormProps } from "../types/components/DeliveryFormProps";
import { Field } from "./Field";
import { Map } from "./Map";
import { Spinner } from "./Spinner";

export const DeliveryFormNew = ({ showFullAddress = false, setShowFullAddress }: DeliveryFormProps) => {
	// const [searchedPostcode, setSearchedPostcode] = useState("");
	const [searchedAddress, setSearchedAddress] = useState("");

	const [lat, setLat] = useState(0);
	const [long, setLong] = useState(0);

	const [addresses, setAddresses] = useState([]);
	const [suggestedAddress, setSuggestedAddress] = useState(false);

	const [showAddressSelect, setShowAddressSelect] = useState(false);
	const [showSuggestedAddress, setShowSuggestedAddress] = useState(false);

	const [loadingList, setLoadingList] = useState(false);
	const [validAddress, setValidAddress] = useState(false);

	const [abortController, setAbortController] = useState<AbortController | null>(null);

	const [postcodeErrorMessage, setPostcodeErrorMessage] = useState("");
	const countryRef = useRef(null);
	const postcodeRef = useRef(null);
	const line1Ref = useRef(null);
	const addressLookupRef = useRef(null);

	const { SetAddress, address, info } = useProductStore(({ SetAddress, address, info }) => {
		return { SetAddress, address, info };
	});

	const isUKPostcode = (postcode) => {
		const ukPostcodeRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?) ?([0-9][A-Z]{2})$/i;
		return ukPostcodeRegex.test(postcode);
	};

	const isPostcodeValid = (postcode) => {
		const countryValue = countryRef.current ? countryRef.current.value : "";

		if (postcode.startsWith("BT") && info?.distributor === "virginia") {
			setPostcodeErrorMessage("This product cannot be delivered to your selected address.");
			return false;
		}

		if (postcode.length >= 5) {
			if (postcode === "") {
				setPostcodeErrorMessage("Postcode is required");
				return false;
			} else if (postcode && !isUKPostcode(postcode) && countryValue === "United Kingdom") {
				setPostcodeErrorMessage("Please enter a valid UK postcode");
				return false;
			}
		}

		setPostcodeErrorMessage("");
		return true;
	};

	useEffect(() => {
		setLoadingList(true);
		setShowAddressSelect(true);
		setAbortController(new AbortController());

		const lookupAddress = async (lookupAddress) => {
			try {
				const { addresses, latitude, longitude } = await AddressApi.LookupPostcode(lookupAddress, abortController.signal);
				if (!addresses?.length) {
					setAddresses([]);
					setLoadingList(false);
					return;
				}
				setAddresses(addresses);
				setLat(latitude || 0);
				setLong(longitude || 0);
				setLoadingList(false);
			} catch (error) {
				if (error.name === "AbortError") {
					return;
				}
			}
		};

		if (searchedAddress.length >= 3) {
			lookupAddress(searchedAddress);
		} else {
			setAddresses([]);
			setShowAddressSelect(false);
			setLoadingList(false);
		}

		return () => {
			// Cleanup function to abort ongoing API requests when the component unmounts or searchedAddress changes
			if (abortController) {
				abortController.abort();
			}
		}
	}, [searchedAddress]);

	return (
		<div className="delivery-form-section">
			{" "}
			<div className="row">
				<div className="col-12 col-md-2">
					<Map lat={lat} long={long} height={150} />
				</div>
				<div className="col-12 col-md-10">
					<Field
						label="Recipient Name"
						value={address.address_name ? address.address_name : ""}
						required={true}
						classes="name-field"
						minLength={1}
						autocomplete="name"
						onChange={(e) => {
							SetAddress({ address_name: e.target.value });
						}}
					/>

					{!showFullAddress && (
						<>
							<div className="row align-items-center">
								<Field
									label="Start typing your address"
									classes={`col-12 address-lookup-field`}
									referenceEl={addressLookupRef}
									value={searchedAddress}
									onChange={(e: React.InputEvent<HTMLInputElement>) => setSearchedAddress(e.currentTarget.value)}
								/>
							</div>
							<div
								className="col-12 show-manually"
								onClick={() => {
									setShowFullAddress(true);
									setShowAddressSelect(false);
									setShowSuggestedAddress(false);
								}}
							>
								Not found your address? Click here to enter it manually.
							</div>
						</>
					)}

					<div className="addresses">
						<div className="row justify-content-space-between">
							{showAddressSelect && (
								<div className="col-12">
									<div className="address-select-container">
										<strong className="address-select-title">
											<FontAwesomeIcon icon={faMagnifyingGlass} />
											Select your address
										</strong>
										{loadingList ? (
											<Spinner />
										) : addresses?.length > 0 ? (
											<div className="address-select scrollable">
												{addresses.map((address, key) => {
														return (
															<div
																key={key}
																className="address"
																onClick={() => {
																	SetAddress({
																		address_line1: `${address?.business_name !== "" ? `${address.business_name}, ` : ""}${address?.line_1}`,
																		address_line2: address.line_2,
																		address_town: address.town,
																		address_county: address.county,
																		address_country: address.country,
																		address_postcode: address.postcode,
																	});
																	setSuggestedAddress(address);
																	setShowAddressSelect(false);
																	setShowSuggestedAddress(false);
																	setShowFullAddress(true);
																}}
															>
																<span className="first-line">{address.display_address}</span>
															</div>
														);
													})}
											</div>
										) : (
											<div className="no-addresses">
												<FontAwesomeIcon icon={faMagnifyingGlass} />
												No addresses found for this postcode
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>

					<div
						className={`${showFullAddress ? "shown" : ""} full-address row align-items-center justify-content-space-between`}
					>
						<Field
							label="Postcode"
							value={address?.address_postcode ? address.address_postcode : ""}
							required={true}
							classes={`col-12 col-md-6 postcode-field`}
							minLength={5}
							autocomplete="postal-code"
							referenceEl={postcodeRef}
							onChange={(e) => {
								e.target.value = e.target.value.toUpperCase();
								isPostcodeValid(e.target.value);
								SetAddress({ address_postcode: e.target.value });
							}}
						/>
						<Field
							label="Line 1"
							value={address.address_line1 ? address.address_line1 : ""}
							required={true}
							classes="col-12 col-md-6 line1-field"
							autocomplete="address-line1"
							minLength={1}
							referenceEl={line1Ref}
							onChange={(e) => {
								SetAddress({ address_line1: e.target.value });
								if (window.innerWidth < 768) {
									setShowFullAddress(true);
								}
							}}
						/>

					<div
						className={`postcode-message ${postcodeErrorMessage && postcodeErrorMessage !== "" ? "alert error active" : ""}`}
					>
						{postcodeErrorMessage}
					</div>
						<Field
							label="Line 2"
							value={address.address_line2 ? address.address_line2 : ""}
							classes="col-12 col-md-6 line2-field"
							autocomplete="address-line2"
							minLength={1}
							onChange={(e) => {
								SetAddress({ address_line2: e.target.value });
							}}
						/>

						<Field
							label="City/Town"
							value={address.address_town ? address.address_town : ""}
							required={true}
							classes="col-12 col-md-6 town-field"
							autocomplete="address-level2"
							minLength={1}
							onChange={(e) => {
								SetAddress({ address_town: e.target.value });
							}}
						/>

						<Field
							label="County"
							value={address.address_county ? address.address_county : ""}
							required={true}
							classes="col-6 county-field"
							autocomplete="address-level1"
							minLength={1}
							onChange={(e) => {
								SetAddress({ address_county: e.target.value });
							}}
						/>

						{Object.values(info.available_countries ?? {}).length > 0 ? (
							Object.values(info.available_countries).length === 1 ? (
								<Field
									referenceEl={countryRef}
									label="Country"
									value={
										address.address_country
											? address.address_country
											: Object.values(info.available_countries)[0]
									}
									required={true}
									classes="col-6 country-field"
									autocomplete="country"
									minLength={1}
									onChange={(e) => {
										SetAddress({ address_country: e.target.value });
									}}
									isEditable={false}
								/>
							) : (
								<div className="field col-6 country-field">
									<select
										value={address.address_country ? address.address_country : ""}
										required={true}
										onChange={(e) => {
											SetAddress({ address_country: e.target.value });
										}}
									>
										<option value=""></option>
										{Object.values(info.available_countries).map((country, index) => (
											<option key={index} value={country}>
												{country}
											</option>
										))}
									</select>
									<label>Country</label>
								</div>
							)
						) : (
							<Field
								label="Country"
								value={address.address_country ? address.address_country : ""}
								required={true}
								classes="col-6 country-field"
								autocomplete="country"
								minLength={1}
								onChange={(e) => {
									SetAddress({ address_country: e.target.value });
								}}
							/>
						)}

						<div className="phone-container col-12">
							<strong>Want to Receive SMS Updates?</strong>
							<p>
								Enter your phone number below and we will send you SMS tracking updates on your gift
								where available.
							</p>
							<hr />
							<Field
								label="Phone"
								value={address.address_phone ? address.address_phone : ""}
								// required={true}
								classes="col-12 col-md-6 phone-field"
								autocomplete="tel"
								minLength={1}
								onChange={(e) => {
									SetAddress({ address_phone: e.target.value });
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DeliveryFormNew;
